import { useEffect, useMemo, useRef } from 'react';

import classNames from 'classnames';
import throttle from 'lodash/throttle';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import { HOME_CATEGORY_NAME_KEY } from '../../config/constants';
import { useElementWidth } from '../../hooks/useElementWidth';
import { useSelectCategory } from '../../hooks/useSelectCategory';
import { DEFAULT_LANGUAGE } from '../../i18n/constants';
import { getUserLanguage } from '../../i18n/helpers';
import { analytics } from '../../services/analytics';
import { useFlag } from '../../services/launchDarkly';
import type { Provider } from '../../stores/categories/CategoriesStore';
import { HOME_CATEGORY_ID } from '../../stores/categories/CategoriesStore';
import { ALL_PROVIDERS_ID } from '../../stores/games-search/GamesSearchStore';
import { useStores } from '../../stores/useStores';
import { ProvidersDropdown } from '../ProvidersDropdown/ProvidersDropdown';
import SearchButtonV2 from '../SearchButton/SearchButtonV2';
import Chevron from '../SVGIcons/Chevron/Chevron';

import styles from './Filters.module.scss';

interface FiltersProps {
  className?: string;
  leftArrowClassName?: string;
  rightArrowClassName?: string;
}

const scroll = (scrollOffset: number, ref: HTMLDivElement | null) => {
  if (!ref) {
    return;
  }
  ref.scrollTo({
    left: ref.scrollLeft + scrollOffset,
    behavior: 'smooth',
  });
};

const listener = throttle(function listener(ev) {
  const { scrollLeft, scrollWidth, clientWidth } = ev.target;
  const el = ev.target.parentNode;

  if (scrollLeft + clientWidth >= scrollWidth - 10) {
    el.classList.add(styles.is_end);
  } else {
    el.classList.remove(styles.is_end);
  }

  if (scrollLeft < 40) {
    el.classList.add(styles.is_start);
  } else {
    el.classList.remove(styles.is_start);
  }
}, 100);

const checkFiltersCategoriesHaveScroll = (divRef: HTMLDivElement | null) => {
  if (!divRef) {
    return false;
  }
  return divRef.scrollWidth > divRef.clientWidth;
};

export const Filters = observer((props: FiltersProps) => {
  const { className, leftArrowClassName, rightArrowClassName } = props;
  const { categoriesStore, coinTypeStore, gamesSearchStore } = useStores();
  const { isSearchInputOpenV2 } = gamesSearchStore;
  const { t, i18n } = useTranslation();
  const language = getUserLanguage(i18n.language);
  const categoryFiltersRef = useRef<HTMLDivElement>(null);

  const { selectCategory } = useSelectCategory();

  const isSearchEnabled = useFlag('search_enabled', true);
  const isProviderSearchEnabled = useFlag('provider_search_enabled', false);

  const selectedProviderId = gamesSearchStore.selectedProviderId;

  const isProviderSelected = selectedProviderId !== ALL_PROVIDERS_ID;

  const handleProviderChange = (provider: Provider) => {
    if (provider.id !== ALL_PROVIDERS_ID) {
      gamesSearchStore.searchByProvider(provider.id, provider.name);
    } else {
      gamesSearchStore.clearProviderSearch();
    }
  };

  useEffect(() => {
    if (isSearchInputOpenV2 && selectedProviderId !== ALL_PROVIDERS_ID) {
      gamesSearchStore.clearProviderSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearchInputOpenV2]);

  const isHomeActive = !isSearchInputOpenV2 && !isProviderSelected && categoriesStore.currentId === HOME_CATEGORY_ID;

  const { elementWidth } = useElementWidth(categoryFiltersRef);

  const isFiltersCategoriesHaveScroll = useMemo(
    () => checkFiltersCategoriesHaveScroll(categoryFiltersRef.current),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [categoriesStore.categoriesIds, elementWidth],
  );

  const handleCategorySelect = (categoryId: number | string, name = '') => {
    void analytics.sendEvent('slide_bar', {
      event_category: 'lobby_page',
      event_type: 'slide_bar',
      event_value: name,
      event_action: 'click',
    });

    if (gamesSearchStore.isProviderSearch) {
      gamesSearchStore.clearProviderSearch();
    }
    selectCategory(categoryId);
  };

  return (
    <div className={classNames(styles.filters, styles.is_start, className)}>
      {isSearchEnabled && <SearchButtonV2 />}
      <button
        className={classNames(
          styles.arrow,
          styles.arrow_left,
          leftArrowClassName,
          !isFiltersCategoriesHaveScroll && styles.hide_arrows,
        )}
        onClick={() => scroll(-300, categoryFiltersRef.current)}
      >
        <Chevron direction="left" />
      </button>
      <div ref={categoryFiltersRef} className={classNames(styles.filters__categories__wrapper)} onScroll={listener}>
        {isProviderSearchEnabled && (
          <ProvidersDropdown selectedProviderId={selectedProviderId} onProviderChange={handleProviderChange} />
        )}
        <button
          className={classNames(styles.filters__item_container, styles.filters__all_games_item_container, {
            [styles.filters__item_container_active]: isHomeActive,
            [styles.filters__item_container_active_gc]: coinTypeStore.coinType === 'GC',
            [styles.filters__item_container_active_sc]: coinTypeStore.coinType === 'SC',
          })}
          data-testid="category-home"
          onClick={() => handleCategorySelect(HOME_CATEGORY_ID, t(HOME_CATEGORY_NAME_KEY))}
        >
          {t(HOME_CATEGORY_NAME_KEY)}
        </button>
        {categoriesStore.categoriesIds.map((id) => {
          const category = categoriesStore.categoriesRegistry[id];

          if (!category?.showInHeader) {
            return null;
          }

          const isActive = !isSearchInputOpenV2 && !isProviderSelected && categoriesStore.currentId === id;
          const categoryTranslatedName = categoriesStore.categoriesRegistry[id].name;
          const categoryName = categoryTranslatedName
            ? categoryTranslatedName[language] || categoryTranslatedName[DEFAULT_LANGUAGE]
            : '';

          return (
            <button
              key={id}
              className={classNames('animation-slide-left', styles.filters__item_container, {
                [styles.filters__item_container_active]: isActive,
                [styles.filters__item_container_active_gc]: coinTypeStore.coinType === 'GC',
                [styles.filters__item_container_active_sc]: coinTypeStore.coinType === 'SC',
              })}
              onClick={() => handleCategorySelect(id, categoryName)}
            >
              {category.name[language] || category.name[DEFAULT_LANGUAGE]}
            </button>
          );
        })}
      </div>
      <button
        className={classNames(
          styles.arrow,
          styles.arrow_right,
          rightArrowClassName,
          !isFiltersCategoriesHaveScroll && styles.hide_arrows,
        )}
        onClick={() => scroll(300, categoryFiltersRef.current)}
      >
        <Chevron direction="right" />
      </button>
    </div>
  );
});
